import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Modal from "@mui/material/Modal";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HistoryIcon from "@mui/icons-material/History";
import RestoreIcon from "@mui/icons-material/Restore";
import {
  formatPublicationEffectiveDate,
  getPublicationDisplayName,
  getPublicationStatus,
  getScenarioDisplayName,
  publicationStatusToneClasses,
} from "./laborModelPublicationStatus";


const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 620,
  maxWidth: "calc(100vw - 32px)",
  maxHeight: "85vh",
  bgcolor: "background.paper",
  border: "2px solid #E2E8F0",
  borderRadius: "15px",
  boxShadow: 24,
  p: 4,
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
};

const changeClasses = {
  added: "bg-emerald-100 text-emerald-800",
  removed: "bg-rose-100 text-rose-800",
  modified: "bg-amber-100 text-amber-800",
};

function formatPublishedAt(value) {
  if (!value) return "Unknown publication time";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const publishedDate = date.toLocaleDateString([], {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
  const publishedTime = date.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
  return `${publishedDate} at ${publishedTime}`;
}

function LaborModelPublicationHistoryModal({
  open,
  onClose,
  publications = [],
  onRestore,
  onView,
  activePublicationUuid,
}) {
  const [selectedUuid, setSelectedUuid] = useState(null);
  const [expandedItems, setExpandedItems] = useState({});
  const [restoring, setRestoring] = useState(false);
  const [restoreResult, setRestoreResult] = useState(null);

  useEffect(() => {
    if (!open) {
      setSelectedUuid(null);
      setExpandedItems({});
      setRestoreResult(null);
      setRestoring(false);
      return;
    }
    if (publications.length > 0) {
      setSelectedUuid((current) => current || publications[0].uuid);
      setExpandedItems((current) => ({
        ...current,
        [publications[0].uuid]: true,
      }));
    }
  }, [open, publications]);

  const selectedPublication = useMemo(
    () => publications.find((publication) => publication.uuid === selectedUuid),
    [publications, selectedUuid]
  );

  const handleRestore = async () => {
    if (!selectedPublication || restoring) return;
    const confirmed = window.confirm(
      `Create a sandbox copy of ${getPublicationDisplayName(selectedPublication)}?\n\n` +
        "The published model and existing sandbox scenarios will not be changed."
    );
    if (!confirmed) return;

    setRestoring(true);
    try {
      const result = await onRestore(selectedPublication);
      setRestoreResult(result || {});
    } catch (error) {
      // The parent displays the API error and leaves the history open for retry.
    } finally {
      setRestoring(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={restoring ? undefined : onClose}
      aria-labelledby="labor-model-history-title"
    >
      <Box sx={modalStyle}>
        <div className="absolute right-2 top-2">
          <IconButton
            aria-label="Close labor model history"
            onClick={onClose}
            size="small"
            disabled={restoring}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>

        <div className="mb-5 flex items-start gap-3 pr-8">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-violet-50 text-violet-700">
            <HistoryIcon aria-hidden="true" sx={{ fontSize: 21 }} />
          </span>
          <div className="min-w-0">
            <h3 id="labor-model-history-title" className="text-xl font-semibold text-slate-900">
              Labor model history
            </h3>
            <p className="mt-0.5 text-sm text-slate-500">
              Review published models and what changed.
            </p>
          </div>
        </div>

        {restoreResult ? (
          <div className="overflow-y-auto py-6">
            <div className="flex items-center text-emerald-700">
              <CheckCircleIcon className="mr-3" />
              <span className="font-medium">
                Created {getScenarioDisplayName(restoreResult.scenario)} from
                this published model.
              </span>
            </div>
            <div className="mt-6 flex justify-end">
              <button
                type="button"
                className="rounded-lg bg-violet-700 px-5 py-2 text-sm font-medium text-white hover:bg-violet-800"
                onClick={onClose}
              >
                Open sandbox scenario
              </button>
            </div>
          </div>
        ) : publications.length === 0 ? (
          <div className="py-10 text-center text-sm text-slate-500">
            No labor models have been published yet.
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto pr-1">
              {publications.map((publication) => {
                const isSelected = selectedUuid === publication.uuid;
                const isExpanded = Boolean(expandedItems[publication.uuid]);
                const changes = publication.changes || [];
                const publicationStatus = getPublicationStatus(
                  publication,
                  activePublicationUuid
                );
                const publicationName = getPublicationDisplayName(publication);
                const sourceScenarioName = publication.source_scenario_name
                  ? getScenarioDisplayName({
                      name: publication.source_scenario_name,
                    })
                  : "";
                const showSourceScenario = Boolean(
                  sourceScenarioName &&
                    sourceScenarioName.toLowerCase() !==
                      publicationName.toLowerCase()
                );
                return (
                  <div
                    key={publication.uuid}
                    className={`mb-3 overflow-hidden rounded-xl border bg-white transition-colors ${
                      isSelected
                        ? "border-violet-300 shadow-sm"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <div
                      className={`flex items-start gap-2 px-3 py-3 ${
                        isSelected ? "bg-violet-50/40" : "bg-white"
                      }`}
                    >
                      <button
                        type="button"
                        aria-pressed={isSelected}
                        className="min-w-0 flex-1 rounded-lg px-2 py-1 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500"
                        onClick={() => setSelectedUuid(publication.uuid)}
                      >
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-semibold text-slate-900">
                            {publicationName}
                          </span>
                          <span
                            className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                              publicationStatusToneClasses[publicationStatus.tone]
                            }`}
                          >
                            {publicationStatus.shortLabel}
                          </span>
                          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                            {publication.change_count || 0} change
                            {Number(publication.change_count || 0) === 1
                              ? ""
                              : "s"}
                          </span>
                        </div>
                        <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                          <span>
                            {publication.effective_date
                              ? `Effective ${formatPublicationEffectiveDate(
                                  publication.effective_date
                                )}`
                              : publicationStatus.label}
                          </span>
                          <span>
                            Published {formatPublishedAt(publication.published_at)}
                            {publication.published_by_name
                              ? ` by ${publication.published_by_name}`
                              : ""}
                          </span>
                        </div>
                        {showSourceScenario ? (
                          <div className="mt-1 text-xs text-slate-500">
                            From {sourceScenarioName}
                          </div>
                        ) : null}
                        {publication.publish_comment ? (
                          <div className="mt-2 text-sm text-slate-600">
                            {publication.publish_comment}
                          </div>
                        ) : null}
                      </button>
                      <button
                        type="button"
                        aria-label={`${isExpanded ? "Hide" : "Show"} changes for ${getPublicationDisplayName(publication)}`}
                        aria-expanded={isExpanded}
                        className="mt-1 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-slate-500 hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500"
                        onClick={(event) => {
                          event.stopPropagation();
                          setExpandedItems((current) => ({
                            ...current,
                            [publication.uuid]: !isExpanded,
                          }));
                        }}
                      >
                        {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </button>
                    </div>

                    {isExpanded && (
                      <div className="border-t border-slate-200 bg-slate-50/70 px-4 py-3.5">
                        <p className="mb-3 text-xs font-semibold text-slate-600">
                          {publication.compared_to_version
                            ? "Changes from the previous model"
                            : "Initial baseline"}
                        </p>
                        {changes.length === 0 ? (
                          <p className="text-sm text-slate-500">No model changes detected.</p>
                        ) : (
                          <div className="space-y-3.5">
                            {changes.map((change, changeIndex) => (
                              <div
                                key={`${change.title}-${changeIndex}`}
                                className="flex items-start gap-3"
                              >
                                <span
                                  className={`mt-0.5 shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${
                                    changeClasses[change.change_type] || changeClasses.modified
                                  }`}
                                >
                                  {change.change_type}
                                </span>
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-slate-800">
                                    {change.title}
                                  </p>
                                  {(change.details || []).length > 0 && (
                                    <ul className="mt-1 list-disc space-y-0.5 pl-4 text-xs leading-5 text-slate-600">
                                      {change.details.map((detail) => (
                                        <li key={detail}>{detail}</li>
                                      ))}
                                    </ul>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="mt-5 border-t border-slate-200 pt-4">
              <p className="mb-3 text-xs text-slate-500">
                Copying creates a new sandbox and does not change published history.
              </p>
              <div className="flex flex-wrap justify-end gap-3">
                <button
                  type="button"
                  className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500"
                  onClick={() => onView(selectedPublication)}
                  disabled={!selectedPublication || restoring}
                >
                  View model
                </button>
                <button
                  type="button"
                  className="flex items-center justify-center rounded-lg bg-violet-700 px-4 py-2.5 text-sm font-medium text-white hover:bg-violet-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 disabled:bg-slate-300"
                  onClick={handleRestore}
                  disabled={!selectedPublication || restoring}
                >
                  <RestoreIcon className="mr-2" fontSize="small" />
                  {restoring ? "Creating…" : "Copy to sandbox"}
                </button>
              </div>
            </div>
          </>
        )}
      </Box>
    </Modal>
  );
}

export default LaborModelPublicationHistoryModal;
